import { DEFAULT_WBS, wbsLabel, wbsSearchText } from "./wbs";
import type { WbsNode } from "./types";
import type { CPMResult, Phase, StatusRow, Task } from "./types";
import { completedDays, milestoneDisplayDay, taskAllowsWeekend } from "./cpm";
import { DEFAULT_PHASES } from "./phases";
import type { PhaseDef } from "./phases";
import type { Shift } from "./shifts";
import { isWeekendDay } from "./projectDate";

/**
 * Turning the schedule into the day-cards the board draws.
 *
 * A task occupies one card per working day, which is what makes partial progress
 * expressible: day 3 of a 4-day task can be done while day 4 is still open. A
 * part-done task is therefore split — its completed cards sit on the days the work
 * actually happened, its remaining cards on the days the schedule has moved them to,
 * with whatever gap reality has opened up in between.
 *
 * Both the Plan and Today views read the board through here, so weekend handling and
 * progress rendering can't drift apart between them.
 */

export interface DayCard {
  task: Task;
  /** Project day this card sits on. */
  day: number;
  /** 1-based position among the task's own working days — the "day 3 of 10" counter. */
  index: number;
  /** Working days the task takes in total, done and remaining together. */
  span: number;
  /** This day's work has been marked done. */
  done: boolean;
  /** Zero slack in the remaining plan. Never true for completed work. */
  critical: boolean;
  /** Which shift this card sits in. Always set, even on a project that doesn't use
   *  shifts — components that don't split the board by shift simply ignore it. A task
   *  with a second team on it (`doubleShift`) contributes one card per shift for the
   *  same day instead of just one — see `taskDays`. */
  shift: Shift;
}

/**
 * The next `count` working days from `start`, skipping weekends unless work is allowed
 * on them, and skipping days already accounted for.
 *
 * Counting actual working days is what the old per-day loop got wrong: it walked from
 * ES to EF inclusive of the weekend it skipped over, so a 2-day task starting on a
 * Friday drew four cards — Fri, Sat, Sun, Mon — two of them on days the scheduler had
 * explicitly refused to place work on.
 */
function workingDaysFrom(start: number, count: number, allowWeekendWork: boolean, exclude: Set<number>): number[] {
  const days: number[] = [];
  if (count <= 0) return days;
  let day = Math.max(0, Math.floor(start + 1e-9));
  // The scheduler can't produce a span anywhere near this; the bound just stops a bad
  // duration from spinning here forever.
  const limit = day + count * 7 + 14;
  while (days.length < count && day < limit) {
    if ((allowWeekendWork || !isWeekendDay(day)) && !exclude.has(day)) days.push(day);
    day++;
  }
  return days;
}

/**
 * Every day-card one task contributes to the board, in day order. A task with a second
 * team on it (`doubleShift`), on a project that actually runs two shifts, contributes
 * *two* cards per day instead of one — one in each shift column, so both teams show up
 * — rather than the single card any other task gets, pinned to whichever shift column
 * `pinnedShift` says (defaulting to shift 1).
 */
export function taskDays(t: Task, r: CPMResult, allowWeekendWork: boolean, twoShifts = false): DayCard[] {
  const own = taskAllowsWeekend(t, allowWeekendWork);
  const done = completedDays(t);
  const es = r.ES[t.id] ?? 0;
  const remaining = r.remaining[t.id] ?? 0;

  const cells: { day: number; done: boolean }[] = done.map((day) => ({ day, done: true }));
  if (!r.complete[t.id]) {
    if (remaining > 1e-9) {
      workingDaysFrom(es, Math.ceil(remaining - 1e-9), own, new Set(done))
        .forEach((day) => cells.push({ day, done: false }));
    } else if (cells.length === 0) {
      // Zero-duration work — a milestone — still occupies the single day it lands on:
      // the day its predecessors actually finished, not the CPM day after.
      cells.push({ day: Math.max(0, milestoneDisplayDay(t, r.ES, r.EF, allowWeekendWork)), done: false });
    }
  }

  cells.sort((a, b) => a.day - b.day);
  const span = cells.length;
  const critical = !!r.critical[t.id];
  const shifts: Shift[] = twoShifts && t.doubleShift ? [1, 2] : [t.pinnedShift === 2 ? 2 : 1];
  return cells.flatMap((c, i) => shifts.map((shift): DayCard => ({ task: t, day: c.day, index: i + 1, span, done: c.done, critical, shift })));
}

/** Every day-card on the board, for every task. */
export function allTaskDays(tasks: Task[], r: CPMResult, allowWeekendWork: boolean, twoShifts = false): DayCard[] {
  const cards: DayCard[] = [];
  tasks.forEach((t) => {
    if (r.ES[t.id] === undefined) return; // not part of the computed network
    taskDays(t, r, allowWeekendWork, twoShifts).forEach((c) => cards.push(c));
  });
  return cards;
}

export type BoardSortKey = "schedule" | "wbs" | "party" | "subcontractor" | "critical";

export const BOARD_SORT_LABEL: Record<BoardSortKey, string> = {
  schedule: "Schedule order",
  wbs: "WBS",
  party: "Responsible party",
  subcontractor: "Subcontractor",
  critical: "Critical path first",
};

/**
 * Orders two cards that land in the same cell (a day, split further by shift and
 * swimlane). `schedule` — the board's long-standing default — is earliest-start first,
 * so a task already under way sits above one just starting that day. Every other key
 * re-sorts by that field first and falls back to schedule order to break ties, so
 * picking e.g. "Responsible party" clusters one party's cards without turning the rest
 * of the cell into an arbitrary shuffle.
 */
export function compareCards(a: DayCard, b: DayCard, key: BoardSortKey, es: Record<string, number>, nodes: WbsNode[] = DEFAULT_WBS): number {
  const bySchedule = (es[a.task.id] ?? 0) - (es[b.task.id] ?? 0);
  switch (key) {
    case "wbs":
      return wbsLabel(a.task, nodes).localeCompare(wbsLabel(b.task, nodes), undefined, { numeric: true }) || bySchedule;
    case "party":
      // Cards with no responsible party sort after named ones rather than to the front.
      return (a.task.responsibleParty || "￿").localeCompare(b.task.responsibleParty || "￿") || bySchedule;
    case "subcontractor":
      // A task can carry several subcontractors; group it by the first, same as the
      // board card's accent color and the PDF export's bar color. No subcontractor
      // sorts after named ones rather than to the front.
      return (a.task.subcontractors[0] || "￿").localeCompare(b.task.subcontractors[0] || "￿") || bySchedule;
    case "critical":
      return (b.critical ? 1 : 0) - (a.critical ? 1 : 0) || bySchedule;
    default:
      return bySchedule;
  }
}

/**
 * How many day columns the board needs. Driven by the cards rather than by projectEnd
 * alone, so a task recorded as running past its plan still has somewhere to be drawn.
 */
export function boardDayCount(cards: DayCard[], projectEnd: number): number {
  let max = Math.ceil(projectEnd - 1e-9);
  cards.forEach((c) => { max = Math.max(max, c.day + 1); });
  return Math.max(1, max);
}

/**
 * Every week the plan covers, as the seven day-indices each one owns — the windows the
 * Week view and each page of the PDF export are drawn from. Weeks are always full, so
 * the last one runs past `dayCount` rather than being cut short: a Thursday finish
 * shouldn't render a three-column week.
 */
export function weeksOfPlan(dayCount: number): number[][] {
  return Array.from({ length: Math.ceil(dayCount / 7) }, (_, w) =>
    Array.from({ length: 7 }, (_, i) => w * 7 + i)
  );
}

export interface PhaseGroup {
  /** Null means "no swimlanes" — every card sits in one flat group. */
  phase: Phase | null;
  cards: DayCard[];
}

/**
 * One day's cards split into phase swimlanes, in the project's phase order, with empty
 * lanes dropped — how both the standup and the phone view lay a day out. A project
 * that doesn't use swimlanes gets a single flat group instead.
 *
 * A card whose task's `phase` doesn't match any of `phases` (renamed or deleted since
 * the task was placed there) still gets its own trailing group, keyed by that raw
 * value — nothing a swimlane edit does should make existing work vanish from the board.
 */
export function groupCardsByPhase(cards: DayCard[], useSwimLanes = true, phases: PhaseDef[] = DEFAULT_PHASES): PhaseGroup[] {
  if (!useSwimLanes) return cards.length ? [{ phase: null, cards }] : [];
  const known = new Set(phases.map((p) => p.name));
  const groups = phases
    .map((p): PhaseGroup => ({ phase: p.name, cards: cards.filter((c) => c.task.phase === p.name) }))
    .filter((g) => g.cards.length > 0);
  const orphanNames = [...new Set(cards.map((c) => c.task.phase).filter((p) => !known.has(p)))];
  orphanNames.forEach((phase) => groups.push({ phase, cards: cards.filter((c) => c.task.phase === phase) }));
  return groups;
}

export function splitList(s: string): string[] {
  return (s ?? "").split(",").map((x) => x.trim()).filter(Boolean);
}

/** Equipment and personnel called for by a set of cards — the board's derived rows. */
export function resourcesForCards(cards: DayCard[]): { equipment: string[]; personnel: string[] } {
  const equipment = new Set<string>();
  const personnel = new Set<string>();
  cards.forEach((c) => {
    splitList(c.task.requiredEquipment).forEach((x) => equipment.add(x));
    splitList(c.task.requiredPersonnel).forEach((x) => personnel.add(x));
  });
  return { equipment: [...equipment].sort(), personnel: [...personnel].sort() };
}

/** Cards that start a task, as opposed to continuing one already under way. */
export function startingCards(cards: DayCard[]): DayCard[] {
  return cards.filter((c) => c.index === 1);
}

export type StatusTone = "good" | "attention" | "unset";

/**
 * How one status-row day-value should read at a glance. Matching the row's `normal`
 * value (whatever counts as "nothing to do here" for that row) reads as good; any
 * other explicit value calls for attention; a day nobody has decided on stays a quiet
 * "unset" rather than either — collapsing that into "attention" would flag every
 * untouched day on a fresh board, and collapsing it into "good" would hide that
 * nobody's actually looked at it.
 */
export function statusTone(row: StatusRow, v: boolean | null): StatusTone {
  if (v === row.normal) return "good";
  if (v === null) return "unset";
  return "attention";
}

/** One status-row day-value as text. An undecided day reads as "—", not as the off
 *  label, so an untouched cell can't be mistaken for a decision someone made. */
export function statusLabel(row: StatusRow, v: boolean | null): string {
  return v == null ? "—" : v ? row.onLabel : row.offLabel;
}

/** Tasks with a card on `day` that depend on one status row, regardless of whether
 *  today's value happens to satisfy them — what a status row's own cell shows so
 *  whoever's about to toggle it sees who's counting on it first. */
export function tasksOnDayRequiringRow(cards: DayCard[], day: number, rowId: string): Task[] {
  const seen = new Set<string>();
  const result: Task[] = [];
  cards.forEach((c) => {
    if (c.day !== day || seen.has(c.task.id)) return;
    if (!c.task.requiredStatus.some((r) => r.rowId === rowId)) return;
    seen.add(c.task.id);
    result.push(c.task);
  });
  return result;
}

/** What the first dependent in `dependents` (see tasksOnDayRequiringRow) requires this
 *  row to be — the suggested default for a day nobody's decided on yet, so an
 *  unconfirmed cell shows a real value instead of a blank dash. */
export function requiredRowValue(dependents: Task[], rowId: string): boolean | null {
  return dependents[0]?.requiredStatus.find((r) => r.rowId === rowId)?.value ?? null;
}

/**
 * A card's site-status requirements that don't hold on the day it sits on — e.g. an
 * E-room card on a day trafo power is still "On" (or not yet decided). Purely a warning:
 * nothing here touches the status row, so a person still has to look and act on it.
 */
export function statusConflicts(card: DayCard, rows: StatusRow[]): { row: StatusRow; wantLabel: string }[] {
  return card.task.requiredStatus
    .map((req) => {
      const row = rows.find((r) => r.id === req.rowId);
      if (!row) return null;
      const actual = row.values[card.day] ?? null;
      if (actual === req.value) return null;
      return { row, wantLabel: req.value ? row.onLabel : row.offLabel };
    })
    .filter((c): c is { row: StatusRow; wantLabel: string } => !!c);
}

export interface BoardFilter {
  text: string;
  critical: boolean;
  blocked: boolean;
  unfinished: boolean;
}

export function filterIsActive(f: BoardFilter): boolean {
  return f.text.trim() !== "" || f.critical || f.blocked || f.unfinished;
}

/**
 * Whether a card is what the board is being asked to find. The text side searches
 * everything a person might reach for — id, name, who's responsible, the subcontractor,
 * the kit — because "where's the cherry picker week" is as real a question as "where's L4".
 */
export function cardMatches(card: DayCard, f: BoardFilter, nodes: WbsNode[] = DEFAULT_WBS): boolean {
  if (f.critical && !card.critical) return false;
  if (f.blocked && !card.task.blocked) return false;
  if (f.unfinished && card.done) return false;
  const q = f.text.trim().toLowerCase();
  if (!q) return true;
  const t = card.task;
  return (
    wbsSearchText(t, nodes).toLowerCase().includes(q) ||
    t.id.toLowerCase().includes(q) ||
    t.name.toLowerCase().includes(q) ||
    (t.responsibleParty ?? "").toLowerCase().includes(q) ||
    (t.subcontractors ?? []).some((s) => s.toLowerCase().includes(q)) ||
    (t.requiredEquipment ?? "").toLowerCase().includes(q) ||
    (t.requiredPersonnel ?? "").toLowerCase().includes(q) ||
    (t.phase ?? "").toLowerCase().includes(q)
  );
}

/** The days holding at least one match, ascending — what "next match" steps through. */
export function matchingDays(cards: DayCard[], f: BoardFilter, nodes: WbsNode[] = DEFAULT_WBS): number[] {
  const set = new Set<number>();
  cards.forEach((c) => { if (cardMatches(c, f, nodes)) set.add(c.day); });
  return [...set].sort((a, b) => a - b);
}
